import { useAdminSender } from "../api";
import type { PlaceSearchResultItem } from "../generatedApi/spotManagerApi";

export interface PlaceWithDuplicateCheck extends PlaceSearchResultItem {
  existingSpotId?: string | null;
  isDuplicateChecking?: boolean;
}

/**
 * place_id/google_cidの重複チェック用カスタムフック
 *
 * スポット作成・編集時に、他のスポットで既にplace_idやcidが使用されているかをチェックします。
 * 編集中のスポット自身（currentSpotId）は重複判定から除外されます。
 */
export const useDuplicateCheck = () => {
  const { sender: searchDraftSpots } = useAdminSender({
    apiName: "admin_search_engine",
    serviceName: "default",
    operationId: "searchDraftSpots"
  });

  const { sender: getPlaceDetails } = useAdminSender({
    apiName: "spot_manager",
    serviceName: "googlePlaces",
    operationId: "getPlaceDetails"
  });

  /**
   * place_idからgoogle_cidを取得
   *
   * @param placeId - Google Place ID
   * @returns google_cid（取得できない場合はnull）
   */
  const fetchCidFromPlaceId = async (placeId: string): Promise<string | null> => {
    try {
      // 最小限のフィールドでPlace Detailsを取得（id のみ = Basic Data SKU）
      const details = await getPlaceDetails({
        placeId,
        fields: "id" // idフィールドのみ取得してコストを最小化
      });

      return details?.google_cid || null;
    } catch (error) {
      console.error(`Failed to fetch CID for place ${placeId}:`, error);
      return null;
    }
  };

  /**
   * 単一のplace_id/cidの重複チェック
   *
   * @param placeId - チェック対象のGoogle Place ID
   * @param cid - チェック対象のGoogle CID（オプション）
   * @param currentSpotId - 現在編集中のスポットID（自分自身を除外するため）
   * @returns 重複している場合は既存スポットのID、重複していない場合はnull
   */
  const checkPlaceDuplicate = async (
    placeId: string,
    cid: string | null | undefined,
    currentSpotId?: string
  ): Promise<string | null> => {
    try {
      // 1. place_idで検索
      const placeIdSearchResult = await searchDraftSpots({
        requestBody: {
          google_place_id: placeId,
          size: 1
        }
      });

      // place_idでヒットした場合は重複あり（ただし現在編集中のスポット自身は除外）
      if (
        placeIdSearchResult &&
        placeIdSearchResult.results &&
        placeIdSearchResult.results.length > 0 &&
        placeIdSearchResult.results[0].spot_id !== currentSpotId
      ) {
        return placeIdSearchResult.results[0].spot_id;
      }

      // 2. place_idでヒットしなかった場合、google_cidでも検索
      if (cid) {
        const cidSearchResult = await searchDraftSpots({
          requestBody: {
            google_cid: cid,
            size: 1
          }
        });

        if (
          cidSearchResult &&
          cidSearchResult.results &&
          cidSearchResult.results.length > 0 &&
          cidSearchResult.results[0].spot_id !== currentSpotId
        ) {
          return cidSearchResult.results[0].spot_id;
        }
      }

      return null; // 重複なし
    } catch (error) {
      console.error(`Duplicate check failed for place ${placeId}:`, error);
      return null; // エラー時は重複なしとして扱う
    }
  };

  /**
   * 複数のplaceを一括で重複チェック
   *
   * バックエンドへの負荷を軽減するため、同時実行数を制限して処理します。
   *
   * @param places - チェック対象のplaceリスト
   * @param currentSpotId - 現在編集中のスポットID（自分自身を除外するため）
   * @returns 重複チェック結果を含むplaceリスト
   */
  const checkPlacesBatch = async (
    places: PlaceSearchResultItem[],
    currentSpotId?: string
  ): Promise<PlaceWithDuplicateCheck[]> => {
    // 同時実行数を制限してバックエンドへの負荷を軽減
    const CONCURRENCY_LIMIT = 10;
    const results: PlaceWithDuplicateCheck[] = [];

    for (let i = 0; i < places.length; i += CONCURRENCY_LIMIT) {
      const batch = places.slice(i, i + CONCURRENCY_LIMIT);

      const batchResults = await Promise.allSettled(
        batch.map(async (place) => {
          const existingSpotId = await checkPlaceDuplicate(
            place.place_id,
            place.google_cid,
            currentSpotId
          );

          return {
            ...place,
            existingSpotId,
            isDuplicateChecking: false
          };
        })
      );

      // fulfilled の結果のみを追加
      batchResults.forEach((result) => {
        if (result.status === "fulfilled") {
          results.push(result.value);
        }
      });
    }

    return results;
  };

  return {
    checkPlaceDuplicate,
    checkPlacesBatch,
    fetchCidFromPlaceId
  };
};
